import { useIntegrationsStore } from './integrations'
import { useTeamsStore } from './teams'
import { useUserStore } from './user'

export const useAuthStore = defineStore('auth', () => {
  const api = useApi()
  const config = useRuntimeConfig()
  const integrationsStore = useIntegrationsStore()
  const teamsStore = useTeamsStore()
  const userStore = useUserStore()

  // Grab opaqueToken state set from `opaque-token.global.ts` middleware
  // We are SURE middleware has safe access to both the state and cookie
  // ensuring we don't get anymore bugs with this feature.
  const opaqueToken = useState<null | string>('opaqueToken')

  // Watch opaqueToken changes and apply them as cookie
  // useCookie is available client-side
  watch(opaqueToken, (newVal) => {
    const cookie = useCookie('opaqueToken')

    if (cookie.value !== newVal) {
      cookie.value = newVal
    }
  })

  const forgotPassword = async (params: {
    email: string
  }) => {
    try {
      await api.auth.password.forgot({ email: params.email })
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const loginWithEmail = async (params: {
    email: string
    invitationToken?: string
    locale?: Locale
    password: string
    recaptchaToken: string
  }) => {
    try {
      const { data: { isNew, opaqueToken, user } } = await api.auth.loginWithEmail({
        email: params.email,
        invitationToken: params.invitationToken,
        locale: params.locale,
        password: params.password,
        recaptchaToken: params.recaptchaToken,
      })

      await loginWithOpaqueToken({
        isNew,
        opaqueToken,
        user,
      })

      return {
        isNew,
        user,
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const loginWithGoogle = async (params: {
    code: string
    invitationToken?: string
    locale?: Locale
  }) => {
    try {
      const { data: { isNew, opaqueToken, user } } = await api.auth.loginWithGoogle({
        code: params.code,
        invitationToken: params.invitationToken,
        locale: params.locale,
      })

      await loginWithOpaqueToken({
        isNew,
        opaqueToken,
        user,
      })

      return {
        isNew,
        user,
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const loginWithOpaqueToken = async (params: {
    isNew: boolean
    opaqueToken: string
    user: User
  }) => {
    try {
      setOpaqueToken(params.opaqueToken)

      if (!params.user.currentTeamId) {
        await teamsStore.createTeam({ name: '' })
      }

      userStore.user = params.user

      await userStore.fetchUserDependencies()

      if (import.meta.client) {
        if (config.public.nuxtEnv === 'production') {
        // Save admin opaque token (useful to avoid traking admins and impersonating users)
          if (teamsStore.currentTeam?.admin) {
            localStorage.setItem('adminOpaqueToken', params.opaqueToken)
          }
        }

        if (params.isNew) {
          triggerChatbox({ trigger: 'product-usage' })
        }
      }

      return params.user
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const logout = async () => {
    try {
      await api.auth.logout()

      // Reset user session
      setOpaqueToken(null)

      integrationsStore.resetStore()
      userStore.resetStore()
      teamsStore.resetStore()
      teamsStore.resetCurrentTeamDependencies()
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const resendVerificationEmail = async (params: {
    email: string
  }) => {
    try {
      const { data: { message } } = await api.auth.verificationEmail.resend({ email: params.email })

      return message
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const resetPassword = async (params: {
    password: string
    passwordConfirmation: string
    passwordResetToken: string
  }) => {
    try {
      const { data: { opaqueToken, user } } = await api.auth.password.reset({
        password: params.password,
        passwordConfirmation: params.passwordConfirmation,
        passwordResetToken: params.passwordResetToken,
      })

      setOpaqueToken(opaqueToken)

      userStore.setUser({ user })

      await userStore.fetchUserDependencies()

      return user
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const verifyVerificationToken = async (params: {
    verificationToken: string
  }) => {
    try {
      const { data: { message, user } } = await api.auth.verificationEmail.verify({ verificationToken: params.verificationToken })

      userStore.user = user

      await userStore.fetchUserDependencies()

      return message
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  // Setters
  const setOpaqueToken = (opaqueTokenValue: null | string) => {
    opaqueToken.value = opaqueTokenValue
  }

  return {
    forgotPassword,
    loginWithEmail,
    loginWithGoogle,
    loginWithOpaqueToken,
    logout,
    opaqueToken,
    resendVerificationEmail,
    resetPassword,
    setOpaqueToken,
    verifyVerificationToken,
  }
})
